import React, { useState } from "react";
import TextField from "../../atoms/TextField/TextField";
import Button from "../../atoms/Button/Button";
import { useTranslation } from "react-i18next";
import LanguageSelect from "../../atoms/LanguageSelect/LanguageSelect";
import FontSizeSelect from "../../atoms/FontSizeSelect/FontSizeSelect";
import ThemeSelect from "../../atoms/ThemeSelect/ThemeSelect";
import SelectField from "../../molecules/SelectField/SelectField";
import { getDoctorsTypeField } from "../../molecules/SettingsCard/helper";
import { Alert } from "@mui/material";
import i18n from "../../../i18n/i18n";
import { verifyPhoneNumber } from "../../../services/helpers/commonHelpers";

export interface UserCreationType {
  name: string;
  email: string;
  type: string;
  phoneNumber: string;
  language: string;
  fontSize: string;
  theme: string;
}

interface FirstLoginFormProps {
  email: string;
  onUserCreate: (body: UserCreationType) => void;
  onLogout: () => void;
}

const FirstLoginForm = (props: FirstLoginFormProps) => {
  const { email, onUserCreate } = props;

  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [title, setTitle] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [doctorType, setDoctorType] = useState("vseobecny");
  const [language, setLanguage] = useState(i18n.language);
  const [fontSize, setFontSize] = useState("normal");
  const [theme, setTheme] = useState("light");
  const [error, setError] = useState<string | undefined>(undefined);

  const handleCreateAccount = () => {
    if (
      name === "" ||
      surname === "" ||
      phoneNumber === "" ||
      doctorType === "" ||
      language === "" ||
      fontSize === "" ||
      theme === ""
    ) {
      setError(t("firstLoginError"));
      return;
    }

    if (verifyPhoneNumber(phoneNumber) === false) {
      setError(t("invalidPhoneNumber"));
      return;
    }

    const displayName = `${title} ${name} ${surname}`;

    const body: UserCreationType = {
      name: displayName,
      email: email,
      type: doctorType,
      phoneNumber,
      language,
      fontSize,
      theme,
    };
    onUserCreate(body);
  };

  return (
    <div className="dui-first-login-fields-wrapper">
      {error && (
        <Alert severity="error" className="dui-first-login-error">
          {error}
        </Alert>
      )}
      <div className="dui-first-login-fields">
        <div className="dui-first-login-field-col">
          <div className="dui-first-login-field">
            <div className="dui-first-login-field-title">{t("title")}:</div>
            <TextField
              value={title}
              onChange={(value) => setTitle(value)}
              placeholder={t("yourTitle")}
            />
          </div>
          <div className="dui-first-login-field">
            <div className="dui-first-login-field-title">
              {t("name")}:<span className="dui-first-login-required"> *</span>
            </div>
            <TextField
              value={name}
              onChange={(value) => setName(value)}
              placeholder={t("yourName")}
            />
          </div>
          <div className="dui-first-login-field">
            <div className="dui-first-login-field-title">
              {t("surname")}:
              <span className="dui-first-login-required"> *</span>
            </div>
            <TextField
              value={surname}
              onChange={(value) => setSurname(value)}
              placeholder={t("yourSurname")}
            />
          </div>
          <div className="dui-first-login-field">
            <div className="dui-first-login-field-title">
              {t("phoneNumber")}:
              <span className="dui-first-login-required"> *</span>
            </div>
            <TextField
              value={phoneNumber}
              onChange={setPhoneNumber}
              placeholder={t("yourPhoneNumber")}
            />
          </div>
        </div>
        <div className="dui-first-login-field-col">
          <div className="dui-first-login-field">
            <div className="dui-first-login-field-title">
              {t("ChooseDoctorType")}:
              <span className="dui-first-login-required"> *</span>
            </div>
            <SelectField
              field={getDoctorsTypeField(t)}
              onChange={setDoctorType}
              value={doctorType}
            />
          </div>

          <div className="dui-first-login-field">
            <div className="dui-first-login-field-title">
              {t("language")}:
              <span className="dui-first-login-required"> *</span>
            </div>
            <LanguageSelect onLangChange={setLanguage} />
          </div>
          <div className="dui-first-login-field">
            <div className="dui-first-login-field-title">
              {t("fontSize")}:
              <span className="dui-first-login-required"> *</span>
            </div>
            <FontSizeSelect onFontSizeChange={setFontSize} />
          </div>
          <div className="dui-first-login-field">
            <div className="dui-first-login-field-title">
              {t("theme")}:<span className="dui-first-login-required"> *</span>
            </div>
            <ThemeSelect onThemeChange={setTheme} />
          </div>
        </div>
      </div>
      <div className="dui-first-login-required-text">
        {t("mandatoryFields")}
        <span className="dui-first-login-required"> *</span>
      </div>
      <div className="dui-first-login-buttons">
        <Button
          onClick={() => handleCreateAccount()}
          className="dui-first-login-button"
          corners="rounded"
        >
          {t("createAccount")}
        </Button>
      </div>
    </div>
  );
};

export default FirstLoginForm;
